import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import billingService from '../../services/BillingService.js';
import userService from '../../services/UserService.js';
import locationService from '../../services/LocationService.js';
import { FileText, Plus, Filter, Download, Eye, Calendar, Pencil, IndianRupee, Trash, DollarSign, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { hasPermission, ROLES } from '../../utils/permissions.js';

const BillingList = () => {
  const [billings, setBillings] = useState([]);
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedBillings, setSelectedBillings] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState({});
  const [emailLoading, setEmailLoading] = useState({});
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const perPage = 10;
  const userRole = user?.role_name || user?.role;
  const canSendInvoice = userRole && [ROLES.SYSTEM_ADMIN, ROLES.ADMIN, ROLES.ACCOUNTANT].some(
    role => role?.toLowerCase() === userRole?.toLowerCase()
  );

  useEffect(() => {
    fetchClients();
    fetchLocations();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchBillings();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, clientFilter, statusFilter, locationFilter, fromDate, toDate]);

  useEffect(() => {
    if (currentPage !== 1) {
      fetchBillings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedBillings([]);
    setSelectAll(false);
    setSelectAllPages(false);
  }, [billings]);

  const fetchClients = async () => {
    try {
      const response = await userService.getClients();
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    }
  };

  const handleSendInvoiceEmail = async (billing) => {
    setEmailLoading(prev => ({ ...prev, [billing.id]: true }));
    try {
      await billingService.sendInvoiceEmail(billing.id);
      Alert.success(`Invoice email sent successfully to ${billing.client?.email || 'client'}!`);
    } catch (err) {
      Alert.error('Failed to send invoice email');
    } finally {
      setEmailLoading(prev => ({ ...prev, [billing.id]: false }));
    }
  };

  const handleDownloadInvoice = async (billing) => {
    setInvoiceLoading(prev => ({ ...prev, [billing.id]: true }));
    try {
      const blob = await billingService.generateInvoice(billing.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${billing.billing_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Invoice downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download invoice');
    } finally {
      setInvoiceLoading(prev => ({ ...prev, [billing.id]: false }));
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations();
      setLocations(response.data || []);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const getPaymentStatusStyle = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-50 border-green-300 text-green-700';
      case 'unpaid':
        return 'bg-red-50 border-red-300 text-red-700';
      case 'partial_paid':
      case 'partial':
        return 'bg-yellow-50 border-yellow-300 text-yellow-700';
      default:
        return 'bg-gray-50 border-gray-300 text-gray-700';
    }
  };

  const getRowBackgroundColor = (billing) => {
    switch (billing.billing_status) {
      case 'paid':        return 'bg-green-50';
      case 'unpaid':      return 'bg-red-50';
      case 'partial_paid':return 'bg-yellow-50';
      default:            return 'bg-white';
    }
  };

  const getPaymentStatus = (billing) => {
    return billing.billing_status || 'unknown';
  };

  const getPaymentStatusBadge = (billing) => {
    const status = billing.billing_status;
    if (!status) return null;

    if (status === 'paid') {
      return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium gap-1 border ${getPaymentStatusStyle('paid')}`}>
          <CheckCircle className="w-3 h-3" />
          Paid
        </span>
      );
    } else if (status === 'unpaid') {
      return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium gap-1 border ${getPaymentStatusStyle('unpaid')}`}>
          <XCircle className="w-3 h-3" />
          Unpaid
        </span>
      );
    } else if (status === 'partial_paid') {
      return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium gap-1 border ${getPaymentStatusStyle('partial_paid')}`}>
          <IndianRupee className="w-3 h-3" />
          Partial
        </span>
      );
    }
    return null;
  };

  const fetchBillings = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (clientFilter) filters.client_id = clientFilter;
      if (statusFilter) filters.status = statusFilter;
      if (locationFilter) filters.location_id = locationFilter;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;

      const response = await billingService.getAll(currentPage, perPage, filters);

      setBillings(response.data || []);
      setTotalPages(response.pagination?.pages || 1);
      setTotalCount(response.pagination?.total || 0);
    } catch (err) {
      Alert.error('Failed to load billings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = async () => {
    if (selectAll || selectAllPages) {
      setSelectedBillings([]);
      setSelectAll(false);
      setSelectAllPages(false);
      return;
    }
    if (totalPages <= 1) {
      setSelectedBillings(billings.map(b => b.id));
      setSelectAll(true);
      return;
    }
    try {
      setSelectAllLoading(true);
      const filters = {};
      if (search) filters.search = search;
      if (clientFilter) filters.client_id = clientFilter;
      if (statusFilter) filters.status = statusFilter;
      if (locationFilter) filters.location_id = locationFilter;
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;
      const response = await billingService.getAllBillings(filters);
      const allIds = (response.data || []).map(b => b.id);
      setSelectedBillings(allIds);
      setSelectAllPages(true);
      setSelectAll(true);
    } catch (err) {
      Alert.error('Failed to select all records');
      console.error(err);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const handleSelectBilling = (billingId) => {
    if (selectedBillings.includes(billingId)) {
      setSelectedBillings(selectedBillings.filter(id => id !== billingId));
      setSelectAll(false);
      setSelectAllPages(false);
    } else {
      const newSelected = [...selectedBillings, billingId];
      setSelectedBillings(newSelected);
      setSelectAllPages(false);
      if (newSelected.length === billings.length) {
        setSelectAll(true);
      }
    }
  };

  const handleDownloadCSV = async () => {
    if (selectedBillings.length === 0) {
      Alert.warning('Please select at least one billing record');
      return;
    }

    try {
      const blob = await billingService.exportCSV(selectedBillings);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `billings_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('CSV exported successfully!');
    } catch (err) {
      Alert.error('Failed to export CSV');
    }
  };

  const handleDeleteBilling = async (billingId, billingCode) => {
    const confirmed = await Alert.confirm(
      `Are you sure you want to delete billing "${billingCode}"? This action cannot be undone.`,
      'Delete Billing'
    );
    if (confirmed) {
      try {
        await billingService.delete(billingId);
        Alert.success('Billing deleted successfully!');
        if (billings.length === 1 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
        } else {
          fetchBillings();
        }
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to delete billing');
      }
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? `${location.city}, ${location.state}` : 'N/A';
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearch('');
    setClientFilter('');
    setStatusFilter('');
    setLocationFilter('');
    setFromDate('');
    setToDate('');
  };

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* Header Section */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <FileText className="w-5 h-5 text-buttonBg" />
                      Billings
                    </h1>
                  </div>
                  <div className="flex gap-2 sm:gap-3">
                    <button
                      onClick={() => navigate('/billings/add')}
                      className="
                            relative overflow-hidden
                            group flex items-center justify-center gap-2
                            px-3 sm:px-3 py-2.5 sm:py-2
                            text-xs sm:text-sm
                            rounded-lg
                            border border-buttonBg
                            bg-buttonBg text-buttonTxt
                            font-medium whitespace-nowrap

                            transition-colors duration-300 ease-out
                            hover:bg-transparent hover:text-buttonBg
                          "
                        >
                          {/* COLLAPSING BACKGROUND */}
                          <span
                            className="
                              absolute inset-0
                              bg-buttonBg
                              scale-x-100
                              group-hover:scale-x-0
                              origin-center
                              transition-transform duration-300 ease-out
                              z-0
                            "
                          />

                          {/* ICON */}
                          <Plus
                            className="
                              w-4 h-4 relative z-10
                              transition-transform duration-300 ease-out
                              group-hover:scale-90
                            "
                          />

                          <span className="relative z-10">Add Billing</span>
                      
                    </button>
                    <button
                      onClick={handleDownloadCSV}
                      disabled={selectedBillings.length === 0}
                      className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Download className="w-4 h-4" />
                      Export CSV
                    </button>
                  </div>
                </div>

                {/* Filters Section */}
                <div className="bg-white p-4 sm:p-6 rounded-sm shadow-sm mb-4 sm:mb-6">
                  <div className="flex items-center justify-between mb-0 sm:mb-4">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-buttonBg font-bold"/>
                      <h3 className="text-base sm:text-lg font-semibold text-tertiary">Filters</h3>
                    </div>
                  </div>
                  {/* Mobile Filter Toggle */}
                  <button
                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                    className="lg:hidden flex items-center gap-2 w-full px-4 py-2 bg-gray-100 rounded-lg text-gray-700 hover:bg-gray-200 transition-colors mb-4"
                  >
                    <Filter className="w-4 h-4" />
                    {showMobileFilters ? 'Hide Filters' : 'Show Filters'}
                  </button>

                  {/* Filter Grid */}
                  <div className={`${showMobileFilters ? 'block' : 'hidden'} lg:block`}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
                      <input
                        type="text"
                        placeholder="Search Billing ID or Description..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-buttonBg focus:border-transparent text-sm"
                      />

                      <select
                        value={clientFilter}
                        onChange={(e) => setClientFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-buttonBg focus:border-transparent text-sm"
                      >
                        <option value="">All Clients</option>
                        {clients.map(client => (
                          <option key={client.id} value={client.id}>{client.name}</option>
                        ))}
                      </select>

                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-buttonBg focus:border-transparent text-sm"
                      >
                        <option value="">All Statuses</option>
                        <option value="paid">Paid</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="partial_paid">Partial</option>
                      </select>

                      <select
                        value={locationFilter}
                        onChange={(e) => setLocationFilter(e.target.value)}
                        className="px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-buttonBg focus:border-transparent text-sm"
                      >
                        <option value="">All Locations</option>
                        {locations.map(location => (
                          <option key={location.id} value={location.id}>
                            {location.city}, {location.state}
                          </option>
                        ))}
                      </select>
                      
                      <div>
                        <div className='relative w-full'>
                          <input
                            type="date"
                            placeholder="From Date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-buttonBg focus:border-transparent text-sm"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>
                      
                      <div>
                        <div className='relative w-full'>
                          <input
                            type="date"
                            placeholder="To Date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="px-4 py-2 w-full border border-gray-300 rounded-sm focus:ring-2 focus:ring-buttonBg focus:border-transparent text-sm"
                          />
                          <Calendar size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-orange pointer-events-none"/>
                        </div>
                      </div>
                    </div>

                    {(search || clientFilter || statusFilter || locationFilter || fromDate || toDate) && (
                      <button
                        onClick={clearFilters}
                        className="text-sm text-buttonBg hover:text-buttonBgHover font-medium"
                      >
                        Clear all filters
                      </button>
                    )}
                  </div>
                </div>


                {/* Selection Actions */}
                {selectedBillings.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center justify-between">
                    <span className="text-sm text-blue-900 font-medium">
                      {selectedBillings.length} billing{selectedBillings.length !== 1 ? 's' : ''} selected
                    </span>
                    <button
                      onClick={() => {
                        setSelectedBillings([]);
                        setSelectAll(false);
                        setSelectAllPages(false);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                    >
                      Clear Selection
                    </button>
                  </div>
                )}

                {/* Desktop Table View */}
                <div className="hidden lg:block">
                  <div className="bg-white rounded-lg shadow-sm">
                    {loading ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buttonBg"></div>
                      </div>
                    ) : billings.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                        <FileText className="w-16 h-16 mb-4 text-gray-300" />
                        <p className="text-lg font-medium">No billings found</p>
                        <p className="text-sm">Create your first billing record to get started</p>
                      </div>
                    ) : (
                      <>
                        <div className="hidden md:block w-full">
                                <table className="w-full table-fixed border-collapse">
                                  <thead>
                                    <tr className="bg-primary text-tertiary">
                                <th className="px-6 py-3 text-left w-[4%]">
                                  <input
                                    type="checkbox"
                                    checked={selectAll || selectAllPages}
                                    onChange={handleSelectAll}
                                    disabled={selectAllLoading}
                                    className={`w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 ${selectAllLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
                                  />
                                </th>
                                <th className="px-6 py-3 w-[15%] text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Billing ID</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 w-[14%] text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Total Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Paid</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Balance</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Location</th>
                                <th className="px-6 py-3 w-[12%] text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {selectAllLoading && (
                                <tr>
                                  <td colSpan="10" className="px-4 py-2 bg-orange-50 text-center text-sm text-orange-800 border-b border-orange-200">
                                    <span className="inline-flex items-center gap-2">
                                      <svg className="animate-spin h-4 w-4 text-orange-600" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                                      Selecting all {totalCount} records...
                                    </span>
                                  </td>
                                </tr>
                              )}
                              {selectAllPages && !selectAllLoading && (
                                <tr>
                                  <td colSpan="10" className="px-4 py-2 bg-orange-50 text-center text-sm text-orange-800 border-b border-orange-200">
                                    All {selectedBillings.length} records across all pages are selected.{' '}
                                    <button onClick={() => { setSelectedBillings([]); setSelectAll(false); setSelectAllPages(false); }} className="font-semibold underline hover:text-orange-600">Clear selection</button>
                                  </td>
                                </tr>
                              )}
                              {billings.map(billing => (
                                <tr 
                                  key={billing.id} 
                                  className={`transition-colors ${getRowBackgroundColor(billing)}`}
                                >
                                  <td className="px-6 py-4">
                                    <input
                                      type="checkbox"
                                      checked={selectedBillings.includes(billing.id)}
                                      onChange={() => handleSelectBilling(billing.id)}
                                      className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                                    />
                                  </td>
                                  <td className="px-6 py-4">
                                    <span
                                      className={`inline-block text-left px-3 py-1.5 rounded-md border-2 text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${getPaymentStatusStyle(getPaymentStatus(billing))}`}
                                      onClick={() => navigate(`/billings/view/${billing.id}`)}
                                    >
                                      {billing.billing_id}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-900">{billing.client?.name || 'Unknown Client'}</td>
                                  <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                                    {formatCurrency(billing.total)}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-green-600 font-semibold">
                                    {formatCurrency(billing.paid)}
                                  </td>
                                  <td className="px-6 py-4 text-sm font-semibold text-red-600">
                                    {formatCurrency(billing.balance_amount)}
                                  </td>
                                  <td className="px-6 py-4">
                                    {getPaymentStatusBadge(billing)}
                                  </td>
                                  <td className="px-6 py-4 text-sm text-gray-600">{getLocationName(billing.location_id)}</td>
                                  <td className="px-6 py-4 text-sm text-gray-600">{formatDate(billing.billing_date)}</td>
                                  <td className="px-6 py-4 relative">
                                    <div className="relative">
                                      <button
                                        onClick={() => setActiveDropdown(activeDropdown === billing.id ? null : billing.id)}
                                        className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-md transition-colors"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                                          <circle cx="12" cy="5" r="2"/>
                                          <circle cx="12" cy="12" r="2"/>
                                          <circle cx="12" cy="19" r="2"/>
                                        </svg>
                                      </button>

                                      {activeDropdown === billing.id && (
                                        <div ref={dropdownRef} className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                                          <button
                                            onClick={() => {
                                              navigate(`/billings/view/${billing.id}`);
                                              setActiveDropdown(null);
                                            }}
                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b"
                                          >
                                            <Eye className="w-4 h-4" />
                                            View Details
                                          </button>
                                          <button
                                            onClick={() => {
                                              handleDownloadInvoice(billing);
                                              setActiveDropdown(null);
                                            }}
                                            disabled={invoiceLoading[billing.id]}
                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b disabled:opacity-60 disabled:cursor-not-allowed"
                                          >
                                            {invoiceLoading[billing.id] ? (
                                              <>
                                                <div className="w-4 h-4 border-2 border-gray-300 border-t-buttonBg rounded-full animate-spin" />
                                                Generating...
                                              </>
                                            ) : (
                                              <>
                                                <Download className="w-4 h-4" />
                                                Download Invoice
                                              </>
                                            )}
                                          </button>
                                          <button
                                            onClick={() => {
                                              navigate(`/billings/edit/${billing.id}`);
                                              setActiveDropdown(null);
                                            }}
                                            className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b"
                                          >
                                            <Pencil className="w-4 h-4" />
                                            Edit
                                          </button>
                                          {canSendInvoice && (
                                            <button
                                              onClick={() => {
                                                handleSendInvoiceEmail(billing);
                                                setActiveDropdown(null);
                                              }}
                                              disabled={emailLoading[billing.id]}
                                              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 transition-colors border-b disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                              {emailLoading[billing.id] ? (
                                                <>
                                                  <Loader2 className="w-4 h-4 animate-spin" />
                                                  Sending...
                                                </>
                                              ) : (
                                                <>
                                                  <FileText className="w-4 h-4" />
                                                  Send Invoice Email
                                                </>
                                              )}
                                            </button>
                                          )}
                                          {billing.billing_status !== 'paid' && (
                                            <button
                                              onClick={() => {
                                                handleDeleteBilling(billing.id, billing.billing_id);
                                                setActiveDropdown(null);
                                              }}
                                              className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                            >
                                              <Trash className="w-4 h-4" />
                                              Delete
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Desktop Pagination */}
                        <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t">
                          <div className="text-sm text-gray-700">
                            Showing {billings.length} billing{billings.length !== 1 ? 's' : ''}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              ← Previous
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                              Page {currentPage} of {totalPages}
                            </span>
                            <button
                              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                              Next →
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Mobile/Tablet Card View */}
                <div className="lg:hidden space-y-3 sm:space-y-4">
                  {loading ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                      Loading billings...
                    </div>
                  ) : billings.length === 0 ? (
                    <div className="bg-white p-8 rounded-lg shadow-sm text-center text-gray-600">No billings found</div>
                  ) : (
                    <>
                      {billings.map((billing) => (
                        <div 
                          key={billing.id} 
                          className={`rounded-lg shadow-sm p-4 sm:p-5 ${getRowBackgroundColor(billing)}`}
                        >
                          <div className="flex items-start gap-3 sm:gap-4 mb-3">
                            <input
                              type="checkbox"
                              checked={selectedBillings.includes(billing.id)}
                              onChange={() => handleSelectBilling(billing.id)}
                              className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                            />
                            <FileText className="w-10 h-10 text-tertiary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-base sm:text-lg">
                                <span
                                  className={`inline-block px-3 py-1.5 rounded-md border-2 text-sm font-semibold cursor-pointer hover:opacity-80 transition-opacity ${getPaymentStatusStyle(getPaymentStatus(billing))}`}
                                  onClick={() => navigate(`/billings/view/${billing.id}`)}
                                >
                                  {billing.billing_id}
                                </span>
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-600 truncate mt-1">
                                {billing.client?.name || 'Unknown Client'}
                              </p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm mb-3">
                            <div>
                              <span className="text-gray-500">Total Amount:</span>
                              <p className="text-gray-900 font-semibold">{formatCurrency(billing.total)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Paid:</span>
                              <p className="text-green-600 font-semibold">{formatCurrency(billing.paid)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Balance:</span>
                              <p className="text-red-600 font-semibold">{formatCurrency(billing.balance_amount)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Status:</span>
                              <div className="mt-1">{getPaymentStatusBadge(billing)}</div>
                            </div>
                            <div>
                              <span className="text-gray-500">Location:</span>
                              <p className="text-gray-900 font-medium">{getLocationName(billing.location_id)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Date:</span>
                              <p className="text-gray-900 font-medium">{formatDate(billing.billing_date)}</p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => navigate(`/billings/view/${billing.id}`)}
                              className="flex-1 px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs sm:text-sm font-medium"
                            >
                              👁️ View
                            </button>
                            <button
                              onClick={() => handleDownloadInvoice(billing)}
                              disabled={invoiceLoading[billing.id]}
                              className="flex-1 px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium disabled:opacity-50"
                            >
                              {invoiceLoading[billing.id] ? '⏳' : '📄'} Download Invoice
                            </button>
                            <button
                              onClick={() => navigate(`/billings/edit/${billing.id}`)}
                              className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-xs sm:text-sm font-medium"
                            >
                              ✏️ Edit
                            </button>
                            {canSendInvoice && (
                              <button
                                onClick={() => handleSendInvoiceEmail(billing)}
                                disabled={emailLoading[billing.id]}
                                className="px-3 sm:px-4 py-2 border border-blue-300 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-xs sm:text-sm font-medium disabled:opacity-50"
                              >
                                {emailLoading[billing.id] ? '⏳' : '📧'} Email Invoice
                              </button>
                            )}
                            {billing.billing_status !== 'paid' && (
                              <button
                                onClick={() => handleDeleteBilling(billing.id, billing.billing_id)}
                                className="px-3 sm:px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors text-xs sm:text-sm font-medium"
                              >
                                🗑️ Delete
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Mobile Pagination */}
                      <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
                        <div className="text-xs sm:text-sm text-gray-700 text-center mb-3">
                          Showing {billings.length} billing{billings.length !== 1 ? 's' : ''} - Page {currentPage} of {totalPages}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                          >
                            ← Previous
                          </button>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="flex-1 px-3 sm:px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition text-xs sm:text-sm font-medium"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingList;